import http from "./http.service";
import Promisable from "./promisable.service";
import { modalActions } from "redux/slices/modal";
import { NavigateFunction } from "react-router-dom";
import { loaderActions } from "redux/slices/loader";
import { driverActions } from "redux/slices/driver";
import { getAppDispatch } from "utils/dispatch.util";
import { formLoaderActions } from "redux/slices/formLoader";
import ObjectService from "utils/object.util";
import { digitFixer } from "utils/digitfixer.util";

const url = "/driver";

const DriverService = {
  addDriver: async (data: any, navigate?: NavigateFunction) => {
    const dispatch = getAppDispatch();
    dispatch?.(formLoaderActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}`, data),
    );

    if (success) {
      const { driver } = success.data.data;
      dispatch?.(driverActions.addDriver(driver));
      navigate?.(`/chauffeur-details/${driver._id}`);
    }

    dispatch?.(formLoaderActions.setLoading(false));
    return [success, error];
  },

  updateDriver: async (id: string, data: any, navigate?: NavigateFunction) => {
    const dispatch = getAppDispatch();
    dispatch?.(formLoaderActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.patch(`${url}/${id}`, data),
    );

    if (success) {
      const { driver } = success.data.data;
      dispatch?.(driverActions.updateDriver({ id, driver }));
      navigate?.(`/chauffeur-details/${id}`);
    }

    dispatch?.(formLoaderActions.setLoading(false));
    return [success, error];
  },
  deleteDriver: async (id: string) => {
    const dispatch = getAppDispatch();
    dispatch?.(formLoaderActions.setLoading(true));
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.delete(`${url}/${id}`),
    );

    if (success) {
      const { driver } = success.data.data;
      dispatch?.(driverActions.setDriver(driver));
      dispatch?.(modalActions.closeModal());
    }

    dispatch?.(formLoaderActions.setLoading(false));
    dispatch?.(modalActions.setLoading(false));

    return [success, error];
  },

  updateDriverPosition: async (id: string, data: { position: string }) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.patch(`${url}/${id}/position`, data),
    );

    if (success) {
      const { driver } = success.data.data;
      dispatch?.(driverActions.setDriver(driver));
      dispatch?.(modalActions.closeModal());
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  enableDriver: async (id: string) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/is_enabled/${id}`),
    );

    if (success) {
      const { driver } = success.data.data;
      dispatch?.(driverActions.setDriver(driver));
      dispatch?.(modalActions.closeModal());
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  getDriver: async (id: string) => {
    const dispatch = getAppDispatch();
    dispatch?.(loaderActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/${id}`),
    );

    if (success) {
      const { driver } = success.data.data;
      dispatch?.(driverActions.setDriver(driver));
      const attendance =
        success.data.data?.attendance || success.data.data?.attendances;
      if (Array.isArray(attendance)) {
        dispatch?.(driverActions.setDriverAttendance(attendance));
      }
    } else dispatch?.(driverActions.setDriver({ data: "Not Found" }));

    dispatch?.(loaderActions.setLoading(false));
    return [success, error];
  },

  getAllDrivers: async (data: any = { all: "true" }) => {
    const dispatch = getAppDispatch();
    dispatch?.(driverActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/get`, data),
    );

    if (success) {
      const { count, drivers } = success.data.data;
      dispatch?.(driverActions.setDrivers({ count, drivers }));
      const driver_ids = drivers.map((d: any) => d._id);
      DriverService.getDriversTodayStatus(driver_ids);
    }
    dispatch?.(driverActions.setLoading(false));
    return [success, error];
  },

  getDriversTodayStatus: async (driver_ids: string[]) => {
    const dispatch = getAppDispatch();

    http.setJWT();

    const [success]: any = await Promisable.asPromise(
      http.post(`${url}/today-status`, { driver_ids }),
    );

    if (success) {
      const { statuses } = success.data.data;
      dispatch?.(driverActions.setDriversTodayStatus(statuses));
    }
  },
  getAllDriversGroupByTrip: async (data: any = { all: "true" }) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/getGroupByTrips`, data),
    );

    if (success) {
      const { drivers } = success.data.data;

      dispatch?.(
        modalActions.updateData({
          table_data: drivers.map((driver: any) => ({
            Name: `${ObjectService.getFullName(driver)}`,
            Phone: driver.phone,
            Email: driver.email,
            Company: driver.partner_id?.company_name,
            Partner: `${ObjectService.getFullName(driver.partner)}`,
            Status: driver.status,
            Ratings: driver.ratings,
            "Total Trips": driver.tripCount,
            "Driven Time (hours)": digitFixer(driver.totalRideDuration),
            "Partner price": digitFixer(driver?.partnerRevenue),
            "Net price": digitFixer(driver?.netRevenue),
            Vat: digitFixer(driver?.VATRevenue),
            "Total Revenue": digitFixer(driver?.grossRevenue),
            "BlackLane Revenue": digitFixer(driver?.blacklaneRevenue),
            "BlackLane Trips": digitFixer(driver?.blacklaneTripCount),
            "Pending Violations": driver?.pendingViolations || 0,
            "Charged to Employee": driver?.chargedToEmployeeViolations || 0,
            "Charged to Company": driver?.chargedToCompanyViolations || 0,
            "Man Power Company": driver?.man_power,
            "Total Violations": driver?.trafficViolationCount || 0,
            "Total Violations Fine": driver?.totalFineAmount || 0,
            "Bank Account Title": driver?.bank_details?.account_title || "",
            "Bank IBAN": driver?.bank_details?.iban || "",
            "Bank BIC": driver?.bank_details?.bic || "",
          })),
        }),
      );
    }
    dispatch?.(modalActions.setLoading(false));

    return [success, error];
  },

  getPartnerDrivers: async (id: string) => {
    const dispatch = getAppDispatch();
    dispatch?.(driverActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/partner/${id}`),
    );

    if (success) {
      const { drivers } = success.data.data;
      dispatch?.(driverActions.setPartnerDrivers(drivers));
    }

    dispatch?.(driverActions.setLoading(false));
    return [success, error];
  },

  uploadDriverDocs: async (id: string, data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();
    http.setMultiPart();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/upload/${id}`, data),
    );

    if (success) {
      const { driver } = success.data.data;
      dispatch?.(driverActions.setDriver(driver));
      dispatch?.(modalActions.closeModal());
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  updateDriverStatus: async (id: string, data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/status/${id}`, data),
    );

    if (success) {
      const { driver } = success.data.data;
      dispatch?.(driverActions.setDriver(driver));
      dispatch?.(modalActions.closeModal());
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  updateDriverDocsStatus: async (id: string, data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/document/status/${id}`, data),
    );

    if (success) {
      const { driver } = success.data.data;
      dispatch?.(driverActions.setDriver(driver));
      dispatch?.(modalActions.closeModal());
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  updateRating: async () => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/update-rating`),
    );

    if (success) dispatch?.(modalActions.closeModal());

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  checkDocumentsExpiry: async () => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/update-expiry`),
    );

    if (success) dispatch?.(modalActions.closeModal());

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  dismissDriverDocumentExpiryAlert: async (
    driverId: string,
    doc_key: string,
  ) => {
    const dispatch = getAppDispatch();

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/${driverId}/document-expiry-alert/dismiss`, {
        doc_key,
      }),
    );

    if (success?.data?.data?.driver) {
      dispatch?.(driverActions.setDriver(success.data.data.driver));
      dispatch?.(driverActions.refresh());
    }

    return [success, error];
  },

  // Group Tracking Methods
  getAllGroupTracking: async (data: any = {}) => {
    const dispatch = getAppDispatch();
    dispatch?.(driverActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/group-tracking/getAll`, data),
    );

    if (success) {
      const { count, driverGroupTrackings } = success.data.data;
      dispatch?.(
        driverActions.setGroupTrackings({ count, driverGroupTrackings }),
      );
    }
    dispatch?.(driverActions.setLoading(false));
    return [success, error];
  },

  getGroupTracking: async (id: string) => {
    const dispatch = getAppDispatch();

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/group-tracking/${id}`),
    );

    if (success) {
      const { driverGroupTracking } = success.data.data;
      dispatch?.(driverActions.setGroupTracking({ driverGroupTracking }));
    }

    return [success, error];
  },

  addGroupTracking: async (data: any, navigate?: NavigateFunction) => {
    const dispatch = getAppDispatch();
    dispatch?.(formLoaderActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/group-tracking`, data),
    );

    if (success) navigate?.("/chauffeurs?tab=1");

    dispatch?.(formLoaderActions.setLoading(false));
    return [success, error];
  },

  updateGroupTracking: async (
    id: string,
    data: any,
    navigate?: NavigateFunction,
  ) => {
    const dispatch = getAppDispatch();
    dispatch?.(formLoaderActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.patch(`${url}/group-tracking/${id}`, data),
    );

    if (success) {
      const { groupTracking } = success.data.data;
      dispatch?.(driverActions.updateGroupTracking({ id, groupTracking }));
      navigate?.("/chauffeurs?tab=1");
    }

    dispatch?.(formLoaderActions.setLoading(false));
    return [success, error];
  },

  deleteGroupTracking: async (id: string) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.delete(`${url}/group-tracking/${id}`),
    );

    if (success) {
      dispatch?.(driverActions.deleteGroupTracking(id));
      dispatch?.(modalActions.closeModal());
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  // Driver Sub Status Methods
  addDriverSubStatus: async (data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/logs`, data),
    );

    if (success) {
      const { log } = success.data.data;
      dispatch?.(driverActions.addDriverSubStatus(log));
      DriverService.getDriverSubStatuses(data.driver_id);
      dispatch?.(modalActions.closeModal());
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  updateDriverSubStatus: async (id: string, data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.patch(`${url}/logs/${id}`, data),
    );

    if (success) {
      const { log } = success.data.data;
      dispatch?.(driverActions.updateDriverSubStatus({ id, log }));
      dispatch?.(modalActions.closeModal());
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },

  getDriverSubStatuses: async (driverId: string) => {
    const dispatch = getAppDispatch();
    dispatch?.(driverActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/logs/${driverId}`),
    );

    if (success) {
      const { logs } = success.data.data;
      dispatch?.(driverActions.setDriverSubStatuses(logs));
    }

    dispatch?.(driverActions.setLoading(false));
    return [success, error];
  },
  getDriverAttendance: async (driverId: string) => {
    const dispatch = getAppDispatch();
    dispatch?.(driverActions.setDriverAttendanceLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`${url}/${driverId}/attendance`),
    );

    if (success) {
      const payload =
        success.data.data?.attendances ||
        success.data.data?.attendance ||
        success.data.data?.records ||
        [];
      dispatch?.(driverActions.setDriverAttendance(payload));
    }

    dispatch?.(driverActions.setDriverAttendanceLoading(false));
    return [success, error];
  },
  addDriverAttendance: async (driverId: string, data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`${url}/${driverId}/attendance`, data),
    );

    if (success) {
      dispatch?.(modalActions.closeModal());
      DriverService.getDriverAttendance(driverId);
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },
  updateDriverAttendance: async (
    driverId: string,
    monthId: string,
    data: any,
  ) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.put(`${url}/${driverId}/attendance/${monthId}`, data),
    );

    if (success) {
      dispatch?.(modalActions.closeModal());
      DriverService.getDriverAttendance(driverId);
    }

    dispatch?.(modalActions.setLoading(false));
    return [success, error];
  },
  exportDriverAttendance: async (driverId: string) => {
    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.get(`/driver/${driverId}/attendance/export`),
    );

    if (success) {
      const pdfUrl = success.data?.data?.url;
      if (pdfUrl) {
        const a = document.createElement("a");
        a.href = pdfUrl;
        a.download = `chauffeur-attendance.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    }

    return [success, error];
  },
  generateDriversReport: async (data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`driver-report/generate`, data),
    );

    if (success) {
      const { report } = success.data.data;
      dispatch?.(driverActions.addDriversReport(report));
    }

    dispatch?.(modalActions.setLoading(false));
    dispatch?.(modalActions.closeModal());

    return [success, error];
  },
  getDriversReports: async (data: any) => {
    const dispatch = getAppDispatch();
    dispatch?.(modalActions.setLoading(true));

    http.setJWT();

    const [success, error]: any = await Promisable.asPromise(
      http.post(`driver-report/all`, data),
    );

    if (success) {
      const payload = success.data.data;

      dispatch?.(driverActions.setDriversReports(payload));
    }

    dispatch?.(modalActions.setLoading(false));

    return [success, error];
  },
};

export default DriverService;
